import { MIPManageInvestorsV2Context } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/MIPManageInvestorsV2Context';
import {
	getMIPDetailsDataForLeaver,
	postLeaverData,
	uploadFile,
} from '@dealstack/frontend/shared/dataExchange/services/mip.service';
import { getSecuritiesDataForMip } from '@dealstack/frontend/shared/dataExchange/services/securities.ts';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import {
	DataContractTct,
	DataCounterpartyTct,
	DataFileTct,
	PostInvestorLeaverInvestorInstance,
	PostInvestorLeaverStatistics,
	InvestmentWorkflowProcessSettingsDTO,
} from '@dealstack/shared/lib/generated';
import { isReviewSignatureType } from '@dealstack/shared/lib/common/signatures';
import { useWrappedRequest } from '@dealstack/frontend/shared/other/react/ApiWrapper';
import { useCommonYupValidation } from '@dealstack/frontend/shared/other/react/form/useCommonYupValidation';
import { yupResolver } from '@hookform/resolvers/yup';
import { useContext, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { useWorkflowProgress } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/WorkflowProgress/WorkflowProgressContext';
import { useQuery } from '@tanstack/react-query';
import { formatUnicorn } from '@dealstack/shared/lib/format/formatUnicorn.ts';
import API_PATHS from '@dealstack/shared/lib/generated/apiPaths.ts';

export const LEAVERS_PERSONAL_DATA_FIELDS = {
	isKycRequired: 'isKycRequired',
	isBankAccountRequired: 'isBankAccountRequired',
};

export type NewLeaverContractsFormValues = {
	contracts: NewLeaverContract[];
};
export type NewLeaverContract = Omit<DataContractTct, 'mipId' | 'createdBy' | 'esignProvider'> & {
	file?: DataFileTct;
	fileInstance?: File;
	local?: boolean;
	counterparties: Array<DataCounterpartyTct>;
	hasWitnesses?: boolean;
};

export type NewLeaverInvestorLocal = PostInvestorLeaverInvestorInstance & {
	id: string;
	contracts: string[];
};

type NewLeaverInvestorsFormValues = {
	investorsListData: NewLeaverInvestorLocal[];
	summary: PostInvestorLeaverStatistics;
};

export type NewLeaverInviteTemplateFormValues = {
	invitationText: string;
};

const INITIAL_STEP = 0;
export const MAX_STEP_NUMBER = 6;

export const useNewLeaversModalController = () => {
	const tMipManage = useTranslationMipManage();
	const { startEmailWorkflow } = useWorkflowProgress();
	const commonYup = useCommonYupValidation();
	const manageInvestorsControllerV2 = useContext(MIPManageInvestorsV2Context);
	const getMIPDetailsDataRequest = useWrappedRequest(getMIPDetailsDataForLeaver);
	const postLeaverRequest = useWrappedRequest(postLeaverData);
	const uploadFileRequest = useWrappedRequest(uploadFile);
	const getSecuritiesDataForMipRequest = useQuery({
		queryKey: [
			formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
				mipId: manageInvestorsControllerV2.mipData?.id as string,
			}),
		],
		queryFn: ({ signal }) =>
			getSecuritiesDataForMip(
				formatUnicorn(API_PATHS.SECURITY_MIP_SECURITIES_MIPID_GET, {
					mipId: manageInvestorsControllerV2.mipData?.id as string,
				}),
				undefined,
				signal
			),
		enabled: !!manageInvestorsControllerV2.mipData?.id,
	});

	const [isCompleting, setIsCompleting] = useState(false);
	const [currentStep, setCurrentStep] = useState(INITIAL_STEP);
	const [disableContinue, setDisableContinue] = useState(false);

	useEffect(() => {
		getMIPDetailsDataRequest.fetch();
	}, []);

	const personalDataRequirementsForm = useForm<InvestmentWorkflowProcessSettingsDTO>({
		defaultValues: {
			isKycRequired: false,
			isBankAccountRequired: false,
		},
	});
	const contractForm = useForm<NewLeaverContractsFormValues>({
		defaultValues: {
			contracts: [],
		},
		mode: 'all',
		resolver: yupResolver(
			Yup.object().shape({
				contracts: Yup.array().of(
					Yup.object().shape({
						name: commonYup.contract_name.required,
					})
				),
			})
		),
	});
	const investorsForm = useForm<NewLeaverInvestorsFormValues>();
	const inviteTemplateForm = useForm<NewLeaverInviteTemplateFormValues>();

	const isMIP1200CaseValid = (() => {
		const investorsFormValues = investorsForm.getValues();
		// d.slaby 18.12.2023 MIP-1200
		const result = investorsFormValues?.investorsListData
			?.map((investor) => {
				const numberOfContracts = investor.contracts.length;
				const totalNumberOfShares = investor.securities.reduce(
					(acc, next) => acc + next.totalNumber,
					0
				);
				if (numberOfContracts > 0 && totalNumberOfShares === 0) {
					return false;
				}
				return true;
			})
			.reduce((acc, next) => acc && next, true);

		return result;
	})();

	const onComplete = async () => {
		if (isCompleting) {
			return;
		}

		setIsCompleting(true);

		const personalDataFormValues = personalDataRequirementsForm.getValues();
		const contractFormFormValues = contractForm.getValues();
		const investorsFormValues = investorsForm.getValues();
		const inviteTemplateFormValues = inviteTemplateForm.getValues();

		const contractsWithFiles = await Promise.all([
			...(contractFormFormValues?.contracts?.map(async (item: NewLeaverContract) => {
				if (!item.fileInstance) {
					return item;
				}
				const formData = new FormData();
				formData.append('file', item.fileInstance);
				formData.append('fileSize', item.fileInstance.size.toString());
				const contract = await uploadFileRequest
					.fetch({
						formData,
					})
					.concludeWithPromise();
				if (!contract?.payload) {
					return item;
				}
				return {
					...item,
					file: contract?.payload,
				};
			}) ?? []),
		]);

		const investorsWithRemovedEmptySecuritiesAndLoans =
			investorsFormValues?.investorsListData?.map((investor) => ({
				...investor,
				securities: investor.securities.filter((security) => security.totalNumber),
				loans: investor.loans.filter((loan) => loan.amount),
			}));

		postLeaverRequest
			.fetch({
				investorAllocations: investorsWithRemovedEmptySecuritiesAndLoans,
				processSettings: personalDataFormValues,
				inviteTemplate: Object.values(inviteTemplateFormValues).length
					? inviteTemplateFormValues
					: undefined,
				contracts: contractsWithFiles?.map((contract) => {
					return {
						...contract,
						requiredWitnesses: contract.hasWitnesses ? 1 : 0,
						counterparties: isReviewSignatureType(contract.signatureType)
							? []
							: contract.counterparties,
					};
				}),
			})
			.onSuccess((response) => {
				setIsCompleting(false);

				showToastV2({
					title: tMipManage(`new_joiner.toast.complete.success`),
				});

				startEmailWorkflow(response.payload?.postLeaverNegativeWorkflowHandle);
			})
			.onUnsafeFinish(() => {
				setIsCompleting(false);

				manageInvestorsControllerV2.refreshDataWithoutProgrammeFilter();
				manageInvestorsControllerV2.setShowNewLeaversModal(false);
			});
	};

	const onNextStepClicked = () => {
		if (currentStep === MAX_STEP_NUMBER) {
			return;
		}
		setCurrentStep(currentStep + 1);
	};
	const onPreviousStepClicked = () => {
		if (currentStep === 0) {
			return;
		}
		setCurrentStep(currentStep - 1);
	};

	const totalStepsToDisplay = MAX_STEP_NUMBER; // Skipping the last page (New Leavers Summary), but adding one back to avoid zero-indexing.
	const currentStepToDisplay = currentStep + 1; // Adding one to avoid zero-indexing.

	const mipData = getMIPDetailsDataRequest.response?.payload;
	const allContracts = [
		...(mipData?.contractFiles ?? []),
		...(contractForm.getValues().contracts ?? []),
	];

	return {
		mipData,
		securities: getSecuritiesDataForMipRequest.data,
		allContracts,
		personalDataRequirementsForm,
		contractForm,
		investorsForm,
		inviteTemplateForm,
		currentStep,
		onNextStepClicked,
		onPreviousStepClicked,
		setDisableContinue,
		disableContinue,
		onComplete,
		setShowNewLeaversModal: manageInvestorsControllerV2.setShowNewLeaversModal,
		loanDefinitions: mipData?.loanDefinitions,
		isMIP1200CaseValid,
		isCompleting,
		totalStepsToDisplay,
		currentStepToDisplay,
	};
};
